import { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusTone = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "released" || s === "started") return "text-[#027A48] bg-[#ECFDF3]";
  if (s === "in planning") return "text-[#026AA2] bg-[#F0F9FF]";
  if (s === "stopped") return "text-[#B54708] bg-[#FFFAEB]";
  return "text-[#475467] bg-[#F2F4F7]";
};

// The SAP ByDesign project an NPD activity is for (Oct 19 2026, user's
// decision - this replaced the Radish Projects list). The id picked here is a
// real SAP ProjectID, and the task chosen next to it is what the SAP
// consumption document is actually booked against.
export const NpdProjectPicker = ({ value, name, onPick, testIdPrefix = "npd-project" }) => {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [includeAll, setIncludeAll] = useState(false);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  const search = useCallback((q, all) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/npd-store-requests/projects`, {
          params: { q, include_all: all, limit: 50 },
        });
        setResults(data.projects || []);
        setError(data.error || null);
        setOpen(true);
      } catch (e) {
        setResults([]);
        setError(e.response?.data?.detail || "Could not read projects from SAP");
      } finally {
        setLoading(false);
      }
    }, 250);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <div className="relative">
      <Input
        value={open ? term : (name ? `${value} · ${name}` : term)}
        placeholder="Search SAP projects..."
        onFocus={() => { setTerm(""); setOpen(true); search("", includeAll); }}
        onChange={(e) => { setTerm(e.target.value); search(e.target.value, includeAll); }}
        className="h-9"
        data-testid={`${testIdPrefix}-search-input`}
      />
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto bg-white border border-[#D0D5DD] rounded-sm shadow-md" data-testid={`${testIdPrefix}-results`}>
          <label className="flex items-center gap-1.5 px-2 py-1.5 border-b border-[#EAECF0] text-[11px] text-[#475467] cursor-pointer">
            <input
              type="checkbox"
              checked={includeAll}
              onChange={(e) => { setIncludeAll(e.target.checked); search(term, e.target.checked); }}
              data-testid={`${testIdPrefix}-include-all`}
            />
            Include closed projects
          </label>
          {loading && <p className="px-2 py-1.5 text-[11px] text-[#667085]">Searching SAP...</p>}
          {error && <p className="px-2 py-1.5 text-[11px] text-[#B42318]" data-testid={`${testIdPrefix}-error`}>{error}</p>}
          {!loading && !error && results.length === 0 && (
            <p className="px-2 py-1.5 text-[11px] text-[#667085]">No project matches that.</p>
          )}
          {results.map((p) => (
            <button
              key={p.project_id}
              type="button"
              onClick={() => { onPick(p); setOpen(false); setTerm(""); }}
              className="w-full text-left px-2 py-1.5 text-[12px] hover:bg-[#F0FDF9] border-b border-[#F2F4F7] last:border-b-0"
              data-testid={`${testIdPrefix}-option-${p.project_id}`}
            >
              <span className="font-mono font-bold">{p.project_id}</span>
              <span className="ml-1.5 font-bold">{p.name}</span>
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${statusTone(p.status)}`}>{p.status || "—"}</span>
              <div className="text-[10px] text-[#98A2B3]">
                {p.task_count ? `${p.task_count} released task${p.task_count === 1 ? "" : "s"}` : "no released tasks"}
                {p.closed ? " · closed in SAP" : ""}
              </div>
            </button>
          ))}
        </div>
      )}
      {open && (
        <button
          type="button"
          className="fixed inset-0 z-10 cursor-default"
          onClick={() => setOpen(false)}
          aria-label="close project search"
          data-testid={`${testIdPrefix}-search-backdrop`}
        />
      )}
    </div>
  );
};
